import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getFamilyGraph, addProxyMember, runMOSES, getHereditaryRisks, getMe } from '../lib/api'
import RiskBar from '../components/RiskBar'
import { UserPlus, RefreshCw } from 'lucide-react'

const RELATIONS = [
  'biological_parent',
  'biological_grandparent',
  'biological_great_grandparent',
  'biological_sibling',
  'half_sibling',
  'adopted_child',
  'adopted_parent',
  'spouse',
]

export default function FamilyGraph() {
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [relation, setRelation] = useState(RELATIONS[0])
  const [displayName, setDisplayName] = useState('')
  const [birthYear, setBirthYear] = useState('')
  const [conditions, setConditions] = useState('')

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => getMe().then(r => r.data) })
  const personId = me?.id

  const { data: graph } = useQuery({
    queryKey: ['family-graph'],
    queryFn: () => getFamilyGraph().then((r) => r.data),
  })

  const { data: risks = [] } = useQuery({
    queryKey: ['risks', personId],
    queryFn: () => getHereditaryRisks().then((r) => r.data),
    enabled: !!personId,
  })

  const addMember = useMutation({
    mutationFn: () =>
      addProxyMember({
        relation,
        display_name: displayName,
        birth_year: birthYear ? parseInt(birthYear) : undefined,
        known_conditions: conditions
          .split(',')
          .map((c) => c.trim())
          .filter(Boolean)
          .map((name) => ({ name })),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['family-graph'] })
      qc.invalidateQueries({ queryKey: ['risks'] })
      setShowForm(false)
      setDisplayName('')
      setConditions('')
      setBirthYear('')
    },
  })

  const moses = useMutation({
    mutationFn: runMOSES,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['risks'] }),
  })

  const members = graph?.members ?? []

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 sm:mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Family Graph</h2>
          <p className="text-sm text-gray-500">Multigenerational health patterns via MOSES</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 px-4 py-2 border border-brand-600 text-brand-600 rounded-lg text-sm font-medium hover:bg-brand-50"
          >
            <UserPlus size={14} /> Add member
          </button>
          <button
            onClick={() => moses.mutate()}
            disabled={moses.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50"
          >
            <RefreshCw size={14} className={moses.isPending ? 'animate-spin' : ''} />
            Run MOSES
          </button>
        </div>
      </div>

      {showForm && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">Add proxy family member</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Relation</label>
              <select
                value={relation}
                onChange={(e) => setRelation(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                {RELATIONS.map((r) => (
                  <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Display name</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g. Grandfather Park"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Birth year (optional)</label>
              <input
                type="number"
                value={birthYear}
                onChange={(e) => setBirthYear(e.target.value)}
                placeholder="1945"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">
                Known conditions (comma-separated)
              </label>
              <input
                type="text"
                value={conditions}
                onChange={(e) => setConditions(e.target.value)}
                placeholder="diabetes, hypertension"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => addMember.mutate()}
              disabled={!displayName || addMember.isPending}
              className="px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50"
            >
              Save
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Members */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
            Family members ({members.length})
          </h3>
          {members.length === 0 ? (
            <p className="text-sm text-gray-400">Add family members to enable pattern mining.</p>
          ) : (
            <div className="space-y-2">
              {members.map((m: {
                id: string
                relation: string
                display_name: string
                birth_year: number | null
                is_proxy: boolean
                weight: number
              }) => (
                <div key={m.id} className="bg-white border border-gray-200 rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{m.display_name}</p>
                    <p className="text-xs text-gray-400">
                      {m.relation.replace(/_/g, ' ')}
                      {m.birth_year ? ` · b. ${m.birth_year}` : ''}
                      {m.is_proxy ? ' · proxy' : ''}
                    </p>
                  </div>
                  <span className="text-xs text-gray-400">{Math.round(m.weight * 100)}% weight</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Hereditary Risks */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
            Hereditary Risk Profile ({risks.length})
          </h3>
          {risks.length === 0 ? (
            <p className="text-sm text-gray-400">
              Add family members and run MOSES to compute hereditary risks.
            </p>
          ) : (
            <div className="space-y-3">
              {risks.map((r: Parameters<typeof RiskBar>[0]['risk']) => (
                <RiskBar key={r.id} risk={r} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
